import { PlaceId, SlapId, StickerDesignId } from '../utils/Primitives';
import { useState, useEffect } from 'react';
import { Address } from 'viem';
import { buildViewUrl, getSiteUrl } from '@/app/utils/shareLinks';

interface GetTheLinkProps {
    placeId: PlaceId | null;
    slapId: SlapId | null;
    stickerDesignId: StickerDesignId | null;
    playerAddress?: Address | null;
}

export default function GetTheLink({ placeId, slapId, stickerDesignId, playerAddress }: GetTheLinkProps) {
    const [showSuccess, setShowSuccess] = useState(false);

    const generateUrl = () => {
        const siteUrl = typeof window !== 'undefined' ? window.location.origin : getSiteUrl();
        const params = new URLSearchParams();

        if (placeId !== null) {
            params.append('place', placeId.toString());
        }
        if (slapId !== null) {
            return buildViewUrl({ resourceType: 'slap', id: slapId, siteUrl });
        } else if (stickerDesignId !== null) {
            return buildViewUrl({ resourceType: 'sticker', id: stickerDesignId, siteUrl });
        } else if (playerAddress !== null && playerAddress !== undefined) {
            params.append('player', playerAddress);
        }

        return `${siteUrl}/?${params.toString()}`;
    };

    const handleCopy = async () => {
        if (typeof navigator === 'undefined') {
            console.warn('Navigator not available');
            return;
        }

        try {
            await navigator.clipboard.writeText(generateUrl());
            setShowSuccess(true);
        } catch (err) {
            console.error('Failed to copy link:', err);
        }
    };

    useEffect(() => {
        if (showSuccess) {
            const timer = setTimeout(() => {
                setShowSuccess(false);
            }, 1500);
            return () => clearTimeout(timer);
        }
    }, [showSuccess]);

    return (
        <div className="flex items-center gap-1">
            <button
                type="button"
                onClick={handleCopy}
                className="text-gray-500 hover:text-gray-700 cursor-pointer"
                title="Copy Link"
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-4 h-4 transition-transform hover:scale-125"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 0 1-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 0 1 1.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 0 0-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 0 1-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 0 0-3.375-3.375h-1.5a1.125 1.125 0 0 1-1.125-1.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H9.75"
                    />
                </svg>
            </button>
            <div className="w-4 h-4 flex items-center justify-center -ml-0.5">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2.5}
                    stroke="currentColor"
                    className={`w-4 h-4 transition-opacity duration-200 ${
                        showSuccess ? 'opacity-100 text-green-500' : 'opacity-0'
                    }`}
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M4.5 12.75l6 6 9-13.5"
                    />
                </svg>
            </div>
        </div>
    );
}
